import {
  createLeafletContext,
  LeafletProvider,
  type LeafletContextInterface,
} from '@react-leaflet/core'
import { Map as LeafletMap } from 'leaflet'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import type { MapContainerProps } from 'react-leaflet'

type SafeMapContainerProps = MapContainerProps & {
  children?: ReactNode
  className?: string
  id?: string
  placeholder?: ReactNode
  style?: CSSProperties
}

export const SafeMapContainer = forwardRef<LeafletMap, SafeMapContainerProps>(
  (
    {
      bounds,
      boundsOptions,
      center,
      children,
      className,
      id,
      placeholder,
      style,
      whenReady,
      zoom,
      ...options
    },
    forwardedRef,
  ) => {
    const mapRef = useRef<LeafletMap | null>(null)
    const [context, setContext] = useState<LeafletContextInterface | null>(null)

    useImperativeHandle(forwardedRef, () => mapRef.current as LeafletMap, [])

    const containerRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (!node || mapRef.current) return

        // Match react-leaflet MapContainer semantics: these props initialize the
        // Leaflet instance once; later view changes should go through map APIs.
        const map = new LeafletMap(node, options)
        mapRef.current = map

        if (center != null && zoom != null) {
          map.setView(center, zoom)
        } else if (bounds != null) {
          map.fitBounds(bounds, boundsOptions)
        }

        if (whenReady != null) {
          map.whenReady(whenReady)
        }

        setContext(createLeafletContext(map))
      },
      [bounds, boundsOptions, center, options, whenReady, zoom],
    )

    useEffect(
      () => () => {
        mapRef.current?.remove()
        mapRef.current = null
        setContext(null)
      },
      [],
    )

    return (
      <div ref={containerRef} className={className} id={id} style={style}>
        {context ? (
          <LeafletProvider value={context}>{children}</LeafletProvider>
        ) : (
          placeholder
        )}
      </div>
    )
  },
)

SafeMapContainer.displayName = 'SafeMapContainer'
